import React, { useState } from 'react';
import { bool, func } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { propTypes } from '../../util/types';
import { pathByRouteName } from '../../util/routes';
import * as validators from '../../util/validators';
import routeConfiguration from '../../routeConfiguration';
import { logout } from '../../ducks/Auth.duck';
import { manageDisableScrolling } from '../../ducks/UI.duck';
import { deleteAccount } from '../../containers/AccountSettingsPage/AccountSettingsPage.duck';
import {
  Avatar,
  Button,
  FieldTextInput,
  Form,
  InlineTextButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  Modal,
  SecondaryButton,
} from '../../components';

import css from './OnboardingAccountMenu.css';

// Account menu for the onboarding pages, which redirect every other page back
// to themselves until onboarding is done. Without it a user on the wrong role
// path has no way out of the flow.
const OnboardingAccountMenuComponent = props => {
  const {
    currentUser,
    deleteAccountInProgress,
    deleteAccountError,
    onLogout,
    onDeleteAccount,
    onManageDisableScrolling,
    intl,
  } = props;

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  // deleteAccountError lives in the AccountSettingsPage slice and is only reset by
  // the next attempt, so hide it until the user submits in this modal session.
  const [hasSubmitted, setHasSubmitted] = useState(false);

  if (!currentUser?.id) {
    return null;
  }

  const handleLogout = () => {
    setIsMenuOpen(false);
    onLogout().then(() => {
      const path = pathByRouteName('LandingPage', routeConfiguration());
      if (typeof window === 'object') {
        window.Intercom('shutdown');
        const appId = process.env.REACT_APP_INTERCOM_APP_KEY;
        window.Intercom('boot', {
          api_base: 'https://api-iam.intercom.io',
          app_id: appId,
        });
      }
      if (typeof window !== 'undefined') {
        window.location = path;
      }
    });
  };

  const openDeleteModal = () => {
    setIsMenuOpen(false);
    setHasSubmitted(false);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDelete = ({ currentPassword }) => {
    setHasSubmitted(true);
    // On success the thunk logs the user out and reloads the landing page.
    return onDeleteAccount(currentPassword);
  };

  const passwordRequired = validators.required(
    intl.formatMessage({ id: 'OnboardingAccountMenu.passwordRequired' })
  );

  const errorMessage =
    hasSubmitted && deleteAccountError
      ? intl.formatMessage({
          id:
            deleteAccountError.status === 403
              ? 'OnboardingAccountMenu.wrongPassword'
              : 'OnboardingAccountMenu.deleteError',
        })
      : null;

  return (
    <>
      <Menu
        className={css.menu}
        isOpen={isMenuOpen}
        onToggleActive={setIsMenuOpen}
        contentPosition="left"
        useArrow={false}
      >
        <MenuLabel className={css.menuLabel}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        </MenuLabel>
        <MenuContent className={css.menuContent}>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.menuItem} onClick={handleLogout}>
              <FormattedMessage id="OnboardingAccountMenu.logout" />
            </InlineTextButton>
          </MenuItem>
          <MenuItem key="deleteAccount">
            <InlineTextButton rootClassName={css.menuItemDanger} onClick={openDeleteModal}>
              <FormattedMessage id="OnboardingAccountMenu.deleteAccount" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>

      <Modal
        id="OnboardingDeleteAccountModal"
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onManageDisableScrolling={onManageDisableScrolling}
        usePortal
        contentClassName={css.modalContent}
      >
        <h2 className={css.modalTitle}>
          <FormattedMessage id="OnboardingAccountMenu.deleteModalTitle" />
        </h2>
        <p className={css.modalInfo}>
          <FormattedMessage id="OnboardingAccountMenu.deleteModalInfo" />
        </p>
        {/* Mounted only while open so a reopened modal starts with an empty password */}
        {isDeleteModalOpen ? (
          <FinalForm
            onSubmit={handleDelete}
            render={({ handleSubmit, invalid }) => (
              <Form onSubmit={handleSubmit}>
                <FieldTextInput
                  className={css.passwordField}
                  type="password"
                  id="onboardingDeleteAccountPassword"
                  name="currentPassword"
                  autoComplete="current-password"
                  label={intl.formatMessage({ id: 'OnboardingAccountMenu.passwordLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'OnboardingAccountMenu.passwordPlaceholder',
                  })}
                  validate={passwordRequired}
                />

                {errorMessage ? <p className={css.modalError}>{errorMessage}</p> : null}

                <div className={css.modalActions}>
                  <SecondaryButton
                    rootClassName={css.cancelButton}
                    type="button"
                    onClick={closeDeleteModal}
                  >
                    <FormattedMessage id="OnboardingAccountMenu.cancel" />
                  </SecondaryButton>
                  <Button
                    rootClassName={css.deleteButton}
                    className={deleteAccountInProgress ? css.deleteInProgress : null}
                    type="submit"
                    inProgress={deleteAccountInProgress}
                    disabled={invalid || deleteAccountInProgress}
                  >
                    <FormattedMessage id="OnboardingAccountMenu.confirmDelete" />
                  </Button>
                </div>
              </Form>
            )}
          />
        ) : null}
      </Modal>
    </>
  );
};

OnboardingAccountMenuComponent.defaultProps = {
  currentUser: null,
  deleteAccountError: null,
};

OnboardingAccountMenuComponent.propTypes = {
  currentUser: propTypes.currentUser,
  deleteAccountInProgress: bool.isRequired,
  deleteAccountError: propTypes.error,
  onLogout: func.isRequired,
  onDeleteAccount: func.isRequired,
  onManageDisableScrolling: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { deleteAccountInProgress, deleteAccountError } = state.AccountSettingsPage;
  return {
    currentUser: state.user.currentUser,
    deleteAccountInProgress,
    deleteAccountError,
  };
};

const mapDispatchToProps = dispatch => ({
  onLogout: () => dispatch(logout()),
  onDeleteAccount: currentPassword => dispatch(deleteAccount(currentPassword)),
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const OnboardingAccountMenu = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(OnboardingAccountMenuComponent);

export default OnboardingAccountMenu;
